/**
 * @krisspy-file
 * @type page
 * @name "CategoriesList"
 * @title "Categories List"
 * @description "View and manage all product categories"
 * @routes ["/categories"]
 * @layout "DashboardLayout"
 * @flowName "category-management"
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Tag,
  Edit,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getCategories } from '../services/categories.service';
import type { Database } from '../lib/database.types';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import LoadingPlaceholder from '../components/LoadingPlaceholder';

type Category = Database['public']['Tables']['categories']['Row'];

export default function CategoriesList() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { defaultLanguage } = useLanguage();
  const { merchantId, locationIds, isPlatformAdmin } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCategories();
  }, [merchantId, locationIds, isPlatformAdmin, defaultLanguage]);

  async function loadCategories() {
    setLoading(true);
    setError(null);

    const { data, error: fetchError } = await getCategories(
      { merchantId, locationIds, isPlatformAdmin },
      {
        active: statusFilter === 'all' ? undefined : statusFilter === 'active',
        searchQuery: searchQuery || undefined,
      },
      defaultLanguage
    );

    if (fetchError) {
      setError(fetchError);
      setLoading(false);
      return;
    }

    if (data) {
      setCategories(data);
    }

    setLoading(false);
  }

  const filteredCategories = categories.filter((category) => {
    const matchesSearch = !searchQuery ||
      category.category_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      category.category_slug.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'active' ? category.active : !category.active);

    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (active: boolean | null) => {
    return active ? 'badge-success' : 'badge-danger';
  };

  return (
    <div className="space-y-2xl animate-fade-in">
      <div className="page-header" data-testid="categories-page-header">
        <div className="page-header-title">
          <h1 className="text-3xl font-bold text-primary mb-sm">{t('categories.title')}</h1>
          <p className="text-secondary">{t('categories.description')}</p>
        </div>
        <div className="page-header-actions" data-testid="categories-page-header-actions">
          <button
          onClick={() => navigate('/categories/create')}
          className="btn btn-primary"
        >
          <Plus className="w-4 h-4" />
          {t('categories.newCategory')}
        </button>
        </div>
      </div>

      <div className="card">
        <div className="flex flex-col md:flex-row gap-lg mb-xl">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary" />
            <input
              type="text"
              placeholder={t('common.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input pl-10 w-full"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input md:w-48"
          >
            <option value="all">{t('inventory.allStatus')}</option>
            <option value="active">{t('common.active')}</option>
            <option value="inactive">{t('common.inactive')}</option>
          </select>

          <button
            onClick={loadCategories}
            className="btn btn-secondary whitespace-nowrap"
          >
            <Search className="w-4 h-4" />
            {t('common.search')}
          </button>
        </div>

        {loading ? (
          <LoadingPlaceholder />
        ) : error ? (
          <div className="text-center py-2xl">
            <div className="w-16 h-16 rounded-full bg-danger/10 mx-auto mb-lg flex items-center justify-center">
              <Tag className="w-8 h-8 text-danger" />
            </div>
            <h3 className="text-lg font-semibold text-primary mb-sm">{t('errors.errorLoading')}</h3>
            <p className="text-secondary mb-xl">{error}</p>
            <button onClick={loadCategories} className="btn btn-primary">
              {t('common.retry')}
            </button>
          </div>
        ) : filteredCategories.length === 0 ? (
          <div className="text-center py-2xl">
            <div className="w-16 h-16 rounded-full bg-tertiary mx-auto mb-lg flex items-center justify-center">
              <Tag className="w-8 h-8 text-secondary" />
            </div>
            <h3 className="text-lg font-semibold text-primary mb-sm">{t('categories.notFound')}</h3>
            <p className="text-secondary mb-xl">
              {searchQuery || statusFilter !== 'all'
                ? t('inventory.tryAdjustingFilters')
                : t('categories.addNew')}
            </p>
            {!searchQuery && statusFilter === 'all' && (
              <button
                onClick={() => navigate('/categories/create')}
                className="btn btn-primary"
              >
                <Plus className="w-4 h-4" />
                {t('categories.newCategory')}
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-lg">
            {filteredCategories.map((category) => (
              <div
                key={category.id}
                className="card-interactive cursor-pointer"
                onClick={() => navigate(`/categories/${category.id}/edit`)}
              >
                <div className="flex items-start justify-between mb-md">
                  {category.picture_url && category.show_picture ? (
                    <img
                      src={category.picture_url}
                      alt={category.category_name}
                      className="w-12 h-12 rounded-lg object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  ) : null}
                  <div className={`w-12 h-12 rounded-lg bg-blue/10 flex items-center justify-center${category.picture_url && category.show_picture ? ' hidden' : ''}`}>
                    <Tag className="w-6 h-6 text-blue" />
                  </div>
                  <span className={`badge ${getStatusBadge(category.active)}`}>
                    {category.active ? t('common.active') : t('common.inactive')}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-primary mb-xs">{category.category_name}</h3>
                <p className="text-sm text-secondary mb-md">{category.category_slug}</p>
                <div className="flex items-center gap-sm pt-md border-t border-primary">
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/categories/${category.id}/edit`); }}
                    className="btn btn-secondary flex-1"
                  >
                    <Edit className="w-4 h-4" />
                    {t('common.edit')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-lg pt-lg border-t border-primary flex items-center justify-between">
          <p className="text-sm text-secondary">
            {t('common.showing')} <span className="font-medium text-primary">{filteredCategories.length}</span> {t('common.of')}{' '}
            <span className="font-medium text-primary">{categories.length}</span> {t('categories.title').toLowerCase()}
          </p>
        </div>
      </div>
    </div>
  );
}
